// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Satellites layer on the browser globes: adds the custom layer to
 * every map (again after a style reload or a new globe), picks a satellite
 * on click, shows its card next to it and follows it as it moves, and puts
 * the small "space events" box over the primary globe.
 */
import type { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl'
import { t, tAttr, tHtml } from '../i18n'
import { formatDate } from '../i18n/format'
import { escapeAttr, escapeHtml } from './domUtils'
import type { SatelliteLayer } from '../services/satellites/satelliteLayer'
import { createSatelliteMapLayer, SATELLITE_MAP_LAYER_ID, type SatelliteMapLayer } from '../services/satellites/satelliteMapLayer'
import { eventFacts, eventKindName, localised, satelliteDatasets, satelliteFacts, showEvent } from '../services/satellites/satelliteText'

export { showEvent }

/** How far out the globe may zoom while satellites float at true scale. */
export const TRUE_SCALE_MIN_ZOOM = -2

export interface SatelliteMapTarget {
  getMap(): MapLibreMap | null
  isFlat(): boolean
}

export interface SatelliteMapUiHandle {
  /** Adds the layer to maps that lack it (call after a layout change). */
  refresh(): void
  destroy(): void
}

export function attachSatellitesToMaps(
  layer: SatelliteLayer,
  targets: () => readonly SatelliteMapTarget[],
  opts: {
    lang: () => string
    primary: () => SatelliteMapTarget | null
    /**
     * The body a globe shows (offEarthBody.ts), null for the Earth. Satellites
     * orbit the Earth (owner, 2026-10-23): a globe of Saturn, the Moon or the
     * Sun draws none, picks none and gets no card. Without it, the layer's own
     * answer (the main globe's body) stands for every globe.
     */
    bodyOf?: (target: SatelliteMapTarget) => string | null
    /** Owner, 2026-10-24: the card lists the datasets the satellite feeds; Load opens one. */
    onLoadDataset?: (id: string) => void
    /** Outside the viewer's plan: the row shows a lock, which opens the plans. */
    datasetLocked?: (id: string) => boolean
    onLockedDataset?: (id: string, trigger: HTMLElement) => void
  },
): SatelliteMapUiHandle {
  const attached = new WeakMap<MapLibreMap, SatelliteMapLayer>()
  const clicks = new Map<MapLibreMap, (e: MapMouseEvent) => void>()
  const targetOf = new WeakMap<MapLibreMap, SatelliteMapTarget>()
  /** On, and this globe shows the Earth. */
  const shownOn = (target: SatelliteMapTarget): boolean => layer.on && (opts.bodyOf ? opts.bodyOf(target) === null : layer.onEarth)
  let card: HTMLElement | null = null
  let cardMap: MapLibreMap | null = null
  let corner: HTMLElement | null = null
  let eventsBox: HTMLElement | null = null
  let eventCard: HTMLElement | null = null
  let eventsDismissed = false

  function keepOnTop(map: MapLibreMap): void {
    const ids = map.getLayersOrder?.() ?? []
    if (ids.length && ids[ids.length - 1] !== SATELLITE_MAP_LAYER_ID && ids.includes(SATELLITE_MAP_LAYER_ID)) {
      try {
        map.moveLayer(SATELLITE_MAP_LAYER_ID)
      } catch {
        /* mid style swap */
      }
    }
  }

  function ensure(target: SatelliteMapTarget): void {
    const map = target.getMap()
    if (!map) return
    targetOf.set(map, target)
    const add = () => {
      if (map.getLayer(SATELLITE_MAP_LAYER_ID)) return
      let impl = attached.get(map)
      if (!impl) {
        impl = createSatelliteMapLayer({ layer, isFlat: () => target.isFlat(), shown: () => shownOn(target) })
        attached.set(map, impl)
      }
      try {
        map.addLayer(impl)
      } catch {
        /* the style is mid-swap; 'styledata' adds it again */
      }
    }
    if (!clicks.has(map)) {
      const onClick = (e: MapMouseEvent) => {
        const impl = attached.get(map)
        if (!impl || !shownOn(target)) return
        const touch = (e.originalEvent as PointerEvent | undefined)?.pointerType === 'touch'
        const hit = impl.pick(map, e.point.x, e.point.y, touch ? 18 : 10)
        if (hit === null) return
        cardMap = map
        layer.select(hit)
      }
      clicks.set(map, onClick)
      map.on('click', onClick)
      // A style reload drops custom layers, and the globe's own layers
      // (the opaque Earth, labels) can be added after ours: stay on top.
      map.on('styledata', () => {
        if (!layer.on) return
        if (!map.getLayer(SATELLITE_MAP_LAYER_ID)) add()
        else keepOnTop(map)
      })
    }
    if (map.isStyleLoaded()) add()
    else map.once('idle', add)
  }

  /** Each map's own lowest zoom while true scale lowered it. */
  const ownMinZoom = new WeakMap<MapLibreMap, number>()

  /**
   * At true scale the geostationary ring is 6.6 globe radii out, wider than
   * the view at the globe's usual lowest zoom: let it zoom out to −2 then
   * (the globe a quarter the size), and give the map its own limit back after.
   */
  function fitZoomRange(): void {
    for (const target of targets()) {
      const map = target.getMap()
      if (!map) continue
      const wide = shownOn(target) && layer.settings.mapHeight === 'true' && !target.isFlat()
      const own = ownMinZoom.get(map)
      if (wide && own === undefined) {
        ownMinZoom.set(map, map.getMinZoom())
        map.setMinZoom(TRUE_SCALE_MIN_ZOOM)
      } else if (!wide && own !== undefined) {
        ownMinZoom.delete(map)
        if (map.getMinZoom() === TRUE_SCALE_MIN_ZOOM) map.setMinZoom(own)
      }
    }
  }

  function refresh(): void {
    fitZoomRange()
    if (!layer.on) return
    for (const target of targets()) ensure(target)
    drawEventsBox()
  }

  function closeCard(): void {
    card?.remove()
    card = null
  }

  function drawCard(): void {
    const index = layer.selected
    const facts = index === null ? null : satelliteFacts(layer, index)
    const target = cardMap ? (targetOf.get(cardMap) ?? null) : opts.primary()
    const map = cardMap ?? target?.getMap() ?? null
    if (!facts || !map || !target || !shownOn(target)) {
      closeCard()
      return
    }
    if (!card || card.parentElement !== map.getContainer()) {
      closeCard()
      card = document.createElement('div')
      card.className = 'sat-map-card'
      card.setAttribute('role', 'dialog')
      // Built once: only the name and the numbers change after, so the ✕
      // under a pointer is never swapped for a new one mid-click.
      card.innerHTML = `
        <button type="button" class="sat-map-card-close" aria-label="${tAttr('satellites.card.close')}">✕</button>
        <h4 class="sat-map-card-name"></h4>
        <dl></dl>
        <div class="sat-map-card-data"></div>
        <p class="sat-map-card-note">${tHtml('satellites.approximate')}</p>`
      card.querySelector('.sat-map-card-close')!.addEventListener('click', () => layer.select(null))
      card.addEventListener('click', (ev) => {
        const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-load], [data-locked]')
        if (!el) return
        ev.stopPropagation()
        if (el.dataset.load) opts.onLoadDataset?.(el.dataset.load)
        else if (el.dataset.locked) opts.onLockedDataset?.(el.dataset.locked, el)
      })
      map.getContainer().appendChild(card)
    }
    const name = card.querySelector('.sat-map-card-name')!
    if (name.textContent !== facts.name) {
      name.textContent = facts.name
      card.setAttribute('aria-label', facts.name)
    }
    const rows = facts.rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('')
    const dl = card.querySelector('dl')!
    if (dl.dataset.rows !== rows) {
      dl.innerHTML = rows
      dl.dataset.rows = rows
    }
    // The datasets it feeds (owner, 2026-10-24), from sources.json.
    const info = index === null ? null : layer.engine.infos[index]
    const data = info ? satelliteDatasets(layer, info.norad) : []
    const dataHtml = data.length
      ? `<h5 class="sat-subtitle">${tHtml('satellites.card.datasets')}</h5>
        <ul class="sat-map-card-datasets">${data
          .map((r) => {
            const locked = r.known && !!opts.datasetLocked?.(r.id)
            return `<li>
              <span class="sat-map-card-dataset-title">${escapeHtml(r.title)}${r.standIn ? ` <span class="sat-map-card-standin">${tHtml('satellites.card.standIn')}</span>` : ''}</span>
              ${r.window ? `<span class="sat-map-card-dataset-window">${escapeHtml(r.window)}</span>` : ''}
              ${locked ? `<button type="button" class="sat-map-card-lock" data-locked="${escapeAttr(r.id)}" title="${tAttr('satellites.card.locked')}" aria-label="${tAttr('satellites.card.locked')}">&#x1F512;&#xFE0E;</button>` : ''}
              ${r.known && opts.onLoadDataset ? `<button type="button" class="sat-chip sat-map-card-load" data-load="${escapeAttr(r.id)}">${tHtml('satellites.card.load')}</button>` : ''}
            </li>`
          })
          .join('')}</ul>
        ${data.some((r) => r.body === 'sun') ? `<p class="sat-map-card-note">${tHtml('satellites.card.showsSun')}</p>` : ''}`
      : ''
    const dataEl = card.querySelector<HTMLElement>('.sat-map-card-data')!
    if (dataEl.dataset.html !== dataHtml) {
      dataEl.innerHTML = dataHtml
      dataEl.dataset.html = dataHtml
    }
    placeCard(map)
  }

  function placeCard(map: MapLibreMap): void {
    const index = layer.selected
    if (!card || index === null) return
    const p = layer.engine.current
    if (p[index * 3] !== p[index * 3]) return
    // Where the layer draws it (on the ground or at its height); away while the Earth hides it.
    const at = attached.get(map)?.screenOf(map, index) ?? null
    card.style.visibility = at ? '' : 'hidden'
    if (!at) return
    const box = map.getContainer().getBoundingClientRect()
    const w = card.offsetWidth || 220
    const h = card.offsetHeight || 160
    // Beside the dot, flipped to stay inside the globe's panel.
    let left = at.x + 14
    if (left + w > box.width - 8) left = at.x - 14 - w
    let top = at.y - h / 2
    top = Math.max(8, Math.min(box.height - h - 8, top))
    // Whole pixels, written only when they change: the card steps rather
    // than shimmers, and stays still under a pointer between steps.
    const px = `${Math.round(Math.max(8, left))}px`
    const py = `${Math.round(top)}px`
    if (card.style.insetInlineStart !== px) card.style.insetInlineStart = px
    if (card.style.insetBlockStart !== py) card.style.insetBlockStart = py
  }

  /**
   * The corner of the primary globe: the open event's card over the short
   * list of events. One box that glides with the globe when Browse or Tools
   * open (--panel-push-view), so it is never under a panel.
   */
  /** The chatbot or the dataset card is open: they own that corner of the window now. */
  function cornerTaken(): boolean {
    const chat = document.getElementById('ayni-chat-panel')
    const info = document.getElementById('info-panel')
    return !!chat && !chat.classList.contains('hidden') || !!info && info.classList.contains('expanded') && !info.classList.contains('hidden')
  }

  function drawEventsBox(): void {
    if (cornerTaken()) layer.closeEvent()
    const primary = opts.primary()
    const map = primary?.getMap()
    const list = layer.events.slice(0, 3)
    const facts = eventFacts(layer, opts.lang())
    const taken = cornerTaken()
    const showList = !!list.length && !eventsDismissed && !taken
    if (!map || !primary || !shownOn(primary) || (!showList && !facts) || taken) {
      corner?.remove()
      corner = null
      eventsBox = null
      eventCard = null
      return
    }
    if (!corner || corner.parentElement !== document.body) {
      corner?.remove()
      corner = document.createElement('div')
      corner.className = 'sat-corner'
      corner.addEventListener('click', (ev) => {
        const el = (ev.target as HTMLElement).closest('button')
        if (!el) return
        if (el.classList.contains('sat-events-box-close')) {
          eventsDismissed = true
          drawEventsBox()
          return
        }
        if (el.classList.contains('sat-event-card-close')) {
          layer.closeEvent()
          return
        }
        if (el.classList.contains('sat-event-card-show')) {
          const at = eventFacts(layer, opts.lang())?.place
          if (at) map.easeTo({ center: [at.lon, at.lat], duration: 1200 })
          return
        }
        if (el.dataset.load) {
          opts.onLoadDataset?.(el.dataset.load)
          return
        }
        if (el.dataset.locked) {
          opts.onLockedDataset?.(el.dataset.locked, el)
          return
        }
        const e = layer.events.find((x) => x.id === el.dataset.event)
        if (e) {
          cardMap = map
          showEvent(layer, e)
        }
      })
      document.body.appendChild(corner)
      eventsBox = null
      eventCard = null
    }
    const lang = opts.lang()
    if (facts) {
      if (!eventCard) {
        eventCard = document.createElement('section')
        eventCard.className = 'sat-event-card'
        eventCard.setAttribute('role', 'dialog')
        corner.prepend(eventCard)
      }
      const html = `
        <button type="button" class="sat-event-card-close" aria-label="${tAttr('satellites.event.close')}">✕</button>
        <p class="sat-event-card-head"><span class="sat-event-kind" data-kind="${escapeAttr(facts.kind)}">${escapeHtml(eventKindName(facts.kind))}</span> <span class="sat-event-date">${escapeHtml(facts.date)}</span></p>
        <h4 class="sat-event-card-title">${escapeHtml(facts.title)}</h4>
        ${facts.text ? `<p class="sat-event-card-text">${escapeHtml(facts.text)}</p>` : ''}
        ${facts.rows.length ? `<dl>${facts.rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('')}</dl>` : ''}
        ${facts.note ? `<p class="sat-map-card-note">${escapeHtml(facts.note)}</p>` : ''}
        ${facts.datasets.length ? `<h5 class="sat-subtitle">${tHtml('satellites.event.datasets')}</h5><ul class="sat-map-card-datasets">${facts.datasets
          .map((r) => {
            const locked = r.known && !!opts.datasetLocked?.(r.id)
            return `<li><span class="sat-map-card-dataset-title">${escapeHtml(r.title)}</span>${locked ? `<button type="button" class="sat-map-card-lock" data-locked="${escapeAttr(r.id)}" title="${tAttr('satellites.card.locked')}" aria-label="${tAttr('satellites.card.locked')}">&#x1F512;&#xFE0E;</button>` : ''}${r.known && opts.onLoadDataset ? `<button type="button" class="sat-chip sat-map-card-load" data-load="${escapeAttr(r.id)}">${tHtml('satellites.card.load')}</button>` : ''}</li>`
          })
          .join('')}</ul>` : ''}
        <p class="sat-event-card-links">
          ${facts.place ? `<button type="button" class="sat-chip sat-event-card-show">${tHtml('satellites.event.show')}</button>` : ''}
          ${facts.source ? `<a href="${escapeAttr(facts.source)}" target="_blank" rel="noopener noreferrer">${tHtml('satellites.event.source')}</a>` : ''}
        </p>`
      if (eventCard.dataset.html !== html) {
        eventCard.innerHTML = html
        eventCard.dataset.html = html
        eventCard.setAttribute('aria-label', facts.title)
      }
    } else if (eventCard) {
      eventCard.remove()
      eventCard = null
    }
    if (!showList) {
      eventsBox?.remove()
      eventsBox = null
      return
    }
    if (!eventsBox) {
      eventsBox = document.createElement('aside')
      eventsBox.className = 'sat-events-box'
      corner.appendChild(eventsBox)
    }
    const html = `
      <h5>${tHtml('satellites.events.title')}</h5>
      <button type="button" class="sat-events-box-close" aria-label="${tAttr('satellites.events.close')}">✕</button>
      <ul>${list
        .map(
          (e) => `<li><button type="button" data-event="${escapeAttr(e.id)}" aria-current="${layer.eventFocus?.event.id === e.id}" title="${escapeAttr(e.text ? localised(e.text, lang) : localised(e.title, lang))}">
            <span class="sat-event-kind" data-kind="${escapeAttr(e.kind)}">${escapeHtml(eventKindName(e.kind))}</span>
            <span class="sat-event-title">${escapeHtml(localised(e.title, lang))}</span>
            <span class="sat-event-date">${escapeHtml(formatDate(e.date, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }))}</span>
          </button></li>`,
        )
        .join('')}</ul>`
    if (eventsBox.dataset.html !== html) {
      eventsBox.innerHTML = html
      eventsBox.dataset.html = html
      eventsBox.setAttribute('aria-label', t('satellites.events.title'))
    }
  }

  // Opening the chatbot or the dataset card closes the event and tucks the list away; closing them brings the list back.
  let wasTaken = cornerTaken()
  const watcher = new MutationObserver(() => {
    const now = cornerTaken()
    if (now === wasTaken) return
    wasTaken = now
    drawEventsBox()
  })
  watcher.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] })

  let lastCardDraw = 0
  const offFrame = layer.onFrame(() => {
    if (layer.selected === null) {
      if (card) closeCard()
      return
    }
    const now = performance.now()
    const map = cardMap ?? opts.primary()?.getMap() ?? null
    // Position every frame, text twice a second.
    if (now - lastCardDraw > 500 || !card) {
      lastCardDraw = now
      drawCard()
    } else if (map) placeCard(map)
  })
  const offChange = layer.onChange(() => {
    refresh()
    drawCard()
    drawEventsBox()
    for (const target of targets()) target.getMap()?.triggerRepaint()
  })
  refresh()

  return {
    refresh,
    destroy() {
      offFrame()
      offChange()
      watcher.disconnect()
      closeCard()
      corner?.remove()
      for (const [map, fn] of clicks) map.off('click', fn)
      clicks.clear()
    },
  }
}
